/**
 * Панель подання — одна для списку, дошки й «Моїх задач» (components.md, розд. 12; аудит якості
 * 2026-10-27, № 11–14). Ті самі слоти в тому самому порядку: назва (кнопка перемикача подань),
 * лічильник, перемикач «Воронка | Список», позначка «• змінено», чипи подань і фільтри, дії.
 *
 * - **Нічого не стрибає між поданнями й виглядами.** Ширина назви — за найдовшою назвою подань
 *   сутності (`useWidestLabel`), слот лічильника є й там, де числа немає (дошка), тож перемикач і
 *   чипи стоять на місці при перемиканні подань і «Воронка ↔ Список» (№ 13, 14).
 * - **Чипи подань — `<OverflowRow>`:** що не вміщується, — у «Ще · N», поточне подання з «Ще» —
 *   останнім у рядку; місцем вони поступаються після дій, перед назвою й чипами фільтра (№ 12).
 * - **Дії — `<OverflowRow>`:** основна дія («Створити …», «Зберегти» змін) не ховається; решта за
 *   `priority` іде в «⋯» перед основною дією, тож на 768 px жодна кнопка не за екраном (№ 11).
 *   Дія з власним меню (дії з поданням, порядок дошки) у «⋯» — групою пунктів.
 */
import type { MetaView } from '@clientline/contracts';
import { type ReactNode, type RefObject, useLayoutEffect, useState } from 'react';
import { list as tl } from '../i18n/uk/list.ts';
import { Icon } from '../ui/Icon.tsx';
import type { MenuItem } from '../ui/Menu.tsx';
import { type OverflowEntry, OverflowRow } from '../ui/OverflowRow.tsx';

/** Чипи панелі: закріплені людиною подання сутності за порядком; немає — подання ролі й платформи. */
export function chipViews(views: readonly MetaView[], current: MetaView): MetaView[] {
  const pinned = views
    .filter((v) => v.prefs?.pinned)
    .sort((a, b) => (a.prefs?.position ?? 0) - (b.prefs?.position ?? 0));
  const chips = pinned.length > 0 ? pinned : views.filter((v) => v.ownerKind !== 'user');
  return chips.some((v) => v.key === current.key) ? chips : [...chips, current];
}

/** Дія з власним меню (дії з поданням, порядок) у «⋯» — групою: назва групи й пункти. */
export function menuGroup(id: string, label: string, items: readonly MenuItem[]): MenuItem[] {
  return items.length > 0 ? [{ kind: 'group', id: `group:${id}`, label }, ...items] : [];
}

/**
 * Найширша з назв у пікселях накреслення елемента `ref`: панель резервує під назву подання місце
 * найдовшої назви сутності, і зміна подання не зсуває сусідів (№ 14). Міряє `OffscreenCanvas` без
 * розкладки; без нього (jsdom, старий браузер) — без резерву.
 */
export function useWidestLabel(
  ref: RefObject<HTMLElement | null>,
  labels: readonly string[],
): number | undefined {
  const [width, setWidth] = useState<number>();
  const key = labels.join('\u0001');
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` — набір назв
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof OffscreenCanvas === 'undefined') return;
    const ctx = new OffscreenCanvas(1, 1).getContext('2d');
    if (!ctx) return;
    let live = true;
    const run = () => {
      if (!live) return;
      const s = getComputedStyle(el);
      ctx.font = `${s.fontStyle} ${s.fontWeight} ${s.fontSize} ${s.fontFamily}`;
      const widest = Math.ceil(Math.max(0, ...labels.map((l) => ctx.measureText(l).width)));
      setWidth((w) => (w === widest ? w : widest));
    };
    run();
    // Шрифт інтерфейсу ще вантажиться — перемірюємо, коли він готовий (інакше резерв за запасним).
    void document.fonts?.ready.then(run);
    return () => {
      live = false;
    };
  }, [ref, key]);
  return width;
}

export interface ViewToolbarProps {
  /** `<h1>` панелі. */
  title: ReactNode;
  /**
   * Лічильник подання — `.toolbar__count`. `null` — числа немає (дошка, ще не прийшов), але місце
   * тримається; `undefined` — слота немає (екран без лічильника).
   */
  count?: ReactNode;
  /** Перемикач «Воронка | Список». */
  layout?: ReactNode;
  /** «• змінено». */
  mark?: ReactNode;
  /** Чипи подань (чи розділів «Моїх задач»). */
  chips: readonly OverflowEntry[];
  /** Ключ поточного чипа: з «Ще» стає в рядок останнім. */
  current: string | null;
  /** Підпис групи чипів; з ним чипи — орієнтир навігації (`nav`). */
  chipsNav?: string | undefined;
  /** Швидкі фільтри й чипи фільтра — після чипів подань. */
  filters?: ReactNode;
  /** Дії в порядку рядка (основна — остання). */
  actions: readonly OverflowEntry[];
  /** Дії, що не ховаються: основна дія екрана. */
  pinned: readonly string[];
  /** Хто з дій займає місце першим (решта йде в «⋯» першою). */
  priority: readonly string[];
  className?: string | undefined;
}

export function ViewToolbar(props: ViewToolbarProps) {
  const className = ['toolbar', 'view-toolbar', props.className].filter(Boolean).join(' ');
  return (
    <div className={className}>
      {props.title}
      {props.count === null ? (
        // Місце лічильника тримається й без числа: перемикач поруч не стрибає (№ 13).
        <span className="toolbar__count" aria-hidden="true" />
      ) : (
        props.count
      )}
      {props.layout}
      {props.mark}
      <div className="toolbar__filters">
        {props.chips.length > 0 && (
          <OverflowRow
            className="view-chips"
            flex
            {...(props.chipsNav ? { as: 'nav' as const, label: props.chipsNav } : {})}
            items={props.chips}
            selected={props.current}
            moreLabel={tl.viewChipsMoreLabel}
            more={(n) => (
              <>
                {tl.viewChipsMore(n)}
                <Icon name="chevron-down" />
              </>
            )}
            moreClassName="chip view-chips__more"
            moreAlign="start"
          />
        )}
        {props.filters}
      </div>
      <OverflowRow
        className="toolbar__actions"
        flex
        items={props.actions}
        pinned={props.pinned}
        priority={props.priority}
        moreLabel={tl.toolbarMoreLabel}
        more={() => <Icon name="more" />}
        moreClassName="btn btn--ghost btn--icon"
        testId="toolbar-actions"
      />
    </div>
  );
}
